<template>
    <div>
        <Breadcrumbs :type="type" :header="title" :url="url"></Breadcrumbs>
        <div class="flex items-center justify-between mb-6">
            <search-filter v-model="form.search" class="mr-4 w-full max-w-xl" @reset="reset">
                <template v-slot:filter>
                    <select v-model="form.trashed" class="form-select  w-28">
                        <option :value="null">Filter</option>
                        <option value="with">With Trashed</option>
                        <option value="only">Only Trashed</option>
                    </select>
                </template>
                <template v-slot:page>
                    <select v-model="form.length" class="form-select  w-20">
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="15">15</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="75">75</option>
                        <option value="100">100</option>
                        <option value="150">150</option>
                        <option value="200">200</option>
                    </select>
                    <select v-model="form.trashed" class="form-select  w-28">
                        <option :value="null">Filter</option>
                        <option value="with">With Trashed</option>
                        <option value="only">Only Trashed</option>
                    </select>
                </template>
                <template v-slot:status>
                    <select v-model="form.status" class="form-select w-32">
                        <option :value="null">Status</option>
                        <option value="1">Active</option>
                        <option value="2">Inactive</option>
                    </select>
                </template>
            </search-filter>
            <Button :type="type" :name="title" :url="createUrl" v-if="permission.add > 0"></Button>
        </div>
        <div class="bg-white rounded-md shadow ">
            <table class="w-full whitespace-nowrap">
                <thead>
                    <tr class="text-left font-bold">
                        <th class="pb-2 pt-3 px-3" width="20">Sl.</th>
                        <th class="pb-2 pt-3 px-3">Info</th>
                        <th class="pb-2 pt-3 px-3 text-center">Status</th>
                        <th class="pb-2 pt-3 px-3 text-center">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(officeGroup, officeName) in groupedCounters">
                        <tr>
                            <td colspan="4" class="bg-gray-200 font-bold px-3 py-2">
                                {{ officeName }}
                            </td>
                        </tr>
                        <tr v-for="(counter, index) in officeGroup" :key="counter.id"
                            class="hover:bg-gray-100 focus-within:bg-gray-100">
                            <td class="border-t pb-1 pt-1 px-3 text-center">
                                {{ (index + counters.from) }}
                            </td>
                            <td class="border-t pb-2 pt-3 px-3">
                                <div class="flex flex-col gap-2">
                                    <p class="font-bold text-md flex items-center">
                                        <icon name="user" class="mr-2 w-4 h-4 fill-gray-700" />
                                        Name: <span class="text-gray-700">{{ counter.title }}</span>
                                    </p>
                                    <p class="text-sm flex items-center">
                                        <icon name="hashtag" class="mr-2 w-4 h-4 fill-gray-700" />
                                        Counter No: <span class="text-gray-700">{{ counter.counter_number }}</span>
                                    </p>
                                    <icon v-if="counter.deleted_at" name="trash" class="ml-2 w-4 h-4 fill-gray-400" />
                                </div>
                            </td>
                            <td class="border-t pb-2 pt-3 px-3 text-center">
                                <status-level :status="counter.status"></status-level>
                            </td>
                            <td class="w-px border-t text-center">
                                <div class="flex items-center px-2" v-if="permission.edit > 0">
                                    <Link class="px-4 py-2" :href="`/backend/queue-counters/${counter.id}/edit`"
                                        tabindex="-1">
                                    <i class="fa fa-edit"></i>
                                    </Link>
                                    <button v-if="!counter.deleted_at" class="px-4 py-2 text-red-600"
                                        @click="softDelete(counter.id)" tabindex="-1">
                                        <i class="fa fa-trash"></i>
                                    </button>
                                    <button v-else class="px-4 py-2 text-orange-600" @click="restore(counter.id)"
                                        tabindex="-1">
                                        <i class="fa fa-undo"></i>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </template>
                    <tr v-if="counters.data.length === 0">
                        <td class="px-6 py-4 border-t" colspan="4">No Data found.</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <pagination class="mt-6" :links="counters.links" />
    </div>
</template>

<script>
import { ref, toRefs, inject } from 'vue'
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchFilter from '@/Shared/SearchFilter'
import Breadcrumbs from '@/Shared/Breadcrumbs'
import StatusLevel from '@/Shared/StatusLevel'
import Button from '@/Shared/Button'
import ToolTip from '@/Shared/ToolTip'

export default {
    components: {
        Head,
        Icon,
        Link,
        Pagination,
        SearchFilter,
        Breadcrumbs,
        StatusLevel,
        Button,
        ToolTip
    },
    layout: Layout,
    props: {
        filters: Object,
        permission: Object,
        counters: Object,
    },
    setup(props) {
        const { filters, counters, permission } = toRefs(props)
        const form = ref({
            search: filters.value.search,
            trashed: filters.value.trashed,
            length: filters.value.length ? filters.value.length : 15,
            status: filters.value.status,
        })
        const type = ref('index')
        const title = ref('Counter')
        const url = ref('')
        const createUrl = ref('/backend/queue-counters/create')
        const reset = () => {
            form.value = mapValues(form, () => null)
        }
        return {
            filters,
            counters,
            form,
            type,
            title,
            url,
            createUrl,
            reset,
            permission
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get('/backend/queue-counters', pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    computed: {
        groupedCounters() {
            return this.counters.data.reduce((groups, counter) => {
                const office = counter.queue_office.office_name || 'Unknown Office';
                if (!groups[office]) {
                    groups[office] = [];
                }
                groups[office].push(counter);
                return groups;
            }, {});
        },
    },
    methods: {
        softDelete(id) {
            this.$swal({
                title: 'Are you sure?',
                text: "You want to delete this counter.",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Delete',
                cancelButtonText: 'Cancel',
            }).then((result) => {
                if (result.isConfirmed) {
                    this.$inertia.delete(`/backend/queue-counters/${id}`);
                    this.$swal.fire('Deleted!', 'The counter has been deleted.', 'success');
                }
            });
        },
        restore(id) {
            this.$swal({
                title: 'Are you sure?',
                text: "You want to restore this counter.",
                icon: 'info',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Restore',
                cancelButtonText: 'Cancel',
            }).then((result) => {
                if (result.isConfirmed) {
                    this.$inertia.put(`/backend/queue-counters/${id}/restore`);
                    this.$swal.fire('Restored!', 'The counter has been restored.', 'success');
                }
            });
        },
    },
}
</script>
